import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { useLocation } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import dayjs from "dayjs"
import { Storage } from "@aws-amplify/storage"
import {
    Button, Card, CardBody, Col, Container, Form, Input,
    Modal, ModalHeader, ModalBody, Row,
} from "reactstrap"

import PlantGrid from "./PlantGrid"
import PlantSelect from "./PlantSelect"

import { getAreas, updateArea, fetchCustomPlants, deleteArea } from "../../store/goals/actions"
import { getProfile } from "../../store/auth/profile/actions"
import { getPlantData, getClimateData, updateDesign, getDesigns } from "../../store/design/actions"
import { addNewEvent, deleteEvent } from "../../store/calendar/actions"
import { createJournalEntry, deleteJournalEntry, getJournalTimeline, updateJournalEntry } from "../../store/journal/actions"

const Spline = require("cubic-spline")

const PlantsInventory = (props) => {
    const {
        userareas, profile, plantData, plantMap, customplants, designs, climateData,
        timelineNotes,
        onGetAreas, onGetProfile, onGetPlantData, onGetClimateData, onLoadDesigns, onGetCustomPlants,
        onUpdateArea, onAddNewEvent, onDeleteEvent, onSaveDesign,
        onCreateJournalEntry, onDeleteJournalEntry, onGetJournalTimeline, onUpdateJournalEntry,
    } = props

    const username = JSON.parse(localStorage.getItem("user") || "{}").user || ""
    const location = useLocation()

    // ── state ────────────────────────────────────────────────────────────────
    const [tableData, setTableData]             = useState([])
    const [currYear, setCurrYear]               = useState(new Date().getFullYear())
    const [showCompleted, setShowCompleted]     = useState(false)
    const [unitmeasurement, setUnitMeasurement] = useState("imperial")
    const [plantProps, setPlantProps]           = useState({})
    const [plantOptions, setPlantOptions]       = useState([])

    // add-plant modal
    const [addPlantModal, setAddPlantModal]                 = useState(false)
    const [currPlant, setCurrPlant]                         = useState({})
    const [currArea, setCurrArea]                           = useState({ plants: [] })
    const [variety, setVariety]                             = useState("")
    const [numPlants, setNumPlants]                         = useState(1)
    const [plantStatus, setPlantStatus]                     = useState("")
    const [seasonRotation, setSeasonRotation]               = useState("")
    const [plantSource, setPlantSource]                     = useState("")
    const [orderDate, setOrderDate]                         = useState("")
    const [expectedDeliveryDate, setExpectedDeliveryDate]   = useState("")
    const [lastDatePossible, setLastDatePossible]           = useState(false)
    const [succession, setSuccession]                       = useState(false)
    const [successionWeeks, setSuccessionWeeks]             = useState("2")   // "2" | "3" | "4" | "consecutive"
    const [errors, setErrors]                               = useState({})
    const calcMetaRef = useRef(null)   // first-planting dates + window end, captured by calcPlantingCalendar
    const [areaSelected, setAreaSelectedState]              = useState("")

    // harvest form (passed to PlantGrid)
    const [harvestDate, setHarvestDate]     = useState("")
    const [harvestAmount, setHarvestAmount] = useState("")
    const [harvestUnit, setHarvestUnit]     = useState("")

    // note form (passed to PlantGrid)
    const [noteDate, setNoteDate]       = useState("")
    const [notes, setNotes]             = useState("")
    const [noteTags, setNoteTags]       = useState("")
    const [selectedFiles, setSelectedFiles] = useState([])

    // planting calendar (needed by calcPlantingCalendar)
    const [climateAverages, setClimateAverages]         = useState([])
    const [plantingCalendarModal, setPlantingCalendarModal] = useState(false)
    const [plantingDates, setPlantingDates]             = useState(false)
    const [plantingMonths, setPlantingMonths]           = useState([])
    const [annotations, setAnnotations]                 = useState({})

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
    const labels = {
        metric:   { weight:"kg",  temperature:"°C", space:"sq. m",  distance:"meters" },
        imperial: { weight:"oz",  temperature:"°F", space:"sq. ft", distance:"inches" },
    }

    var editMode = false
    var newCellData
    var selectedPlantId
    var areaIndices = {}

    // ── mount ────────────────────────────────────────────────────────────────
    useEffect(() => {
        onGetProfile(username)
        onGetPlantData()
        onGetAreas()
        onLoadDesigns()
        onGetCustomPlants()
        onGetJournalTimeline({ username })
        setCurrYear(new Date().getFullYear())
    }, [])

    useEffect(() => {
        if (location.state?.openAddPlant) addPlantToSpreadsheet()
    }, [])

    useEffect(() => {
        if (profile?.unitmeasurement?.length > 0) setUnitMeasurement(profile.unitmeasurement)
        if (profile?.address) {
            onGetClimateData(profile.address.zipcode)
            setPlantProps({
                daysToTransplant: "Days For Seedling",
                numPlants: "Number Of Plants",
                daysToHarvest: "Days To Harvest",
                germinationTime: "Germination Time",
                minimumTemp: `Minimum Temperature (${labels[unitmeasurement].temperature})`,
                maximumTemp: `Maximum Temperature (${labels[unitmeasurement].temperature})`,
                yield_per_plant_ounces: `Yield Per Plant (${labels[unitmeasurement].weight})`,
                plant_family: "Plant Family", season: "Growing Cycle", size: "Size",
                foodForestLayer: "Food Forest Layer", guildFunction: "Guild Function",
                climate: "Climate", USDAMax: "USDA Max Zone", USDAMin: "USDA Min Zone",
                spacing_between_plants: `Spacing between Plants (${labels[unitmeasurement].distance})`,
            })
        }
    }, [profile])

    useEffect(() => {
        const confirmed = plantData.map(x => ({ value: x.name, text: x.name }))
        const custom    = customplants.map(x => ({ value: x.species, text: `${x.species} (custom)` }))
        setPlantOptions([...confirmed, ...custom])
    }, [plantData, customplants])

    // rebuild tableData whenever areas or currYear changes
    useEffect(() => {
        const plantTable = []
        let areaCount = 0
        for (const area of userareas) {
            areaIndices[area.id] = areaCount++
            if (!area.info) area.info = { totalSpace: 0 }
            if (!area.year) area.year = new Date().getFullYear()
            if (area.year !== currYear) continue
            for (const plant of area.plants) {
                plantTable.push(normalizePlant(plant, area.name))
                if (!plant.notes) plant.notes = []
            }
        }
        setTableData(plantTable)
    }, [userareas, currYear])

    // ── helpers ──────────────────────────────────────────────────────────────
    function ensureNumericFields(plant, fields) {
        const fixed = { ...plant }
        fields.forEach(f => {
            const v = fixed[f]
            if (typeof v === "string") { const n = parseFloat(v); fixed[f] = Number.isFinite(n) ? n : null }
        })
        return fixed
    }

    function ensureLowerCase(plant, fields) {
        const fixed = { ...plant }
        fields.forEach(f => {
            const v = fixed[f]
            if (typeof v === "string") fixed[f] = v.toLowerCase()
            else if (Array.isArray(v)) fixed[f] = v.map(x => typeof x === "string" ? x.toLowerCase() : x)
        })
        return fixed
    }

    function normalizePlant(plant, areaName) {
        const numericFields = ["spaceUsed","estHarvest","yield_per_plant_ounces","minimumTemp","maximumTemp","spacing_between_plants","daysToHarvest","numPlants"]
        let cleaned = ensureNumericFields(plant, numericFields)
        cleaned = ensureLowerCase(cleaned, ["season"])
        const normalized = { ...cleaned, area: areaName, id: cleaned.id, guildFunction: cleaned.guildFunction || [], foodForestLayer: cleaned.foodForestLayer || [], climate: cleaned.climate || [] }
        if (unitmeasurement === "metric") {
            normalized.spaceUsed             = (cleaned.spaceUsed / 10.764).toFixed(2)
            normalized.estHarvest            = (cleaned.estHarvest / 2.2).toFixed(2)
            normalized.yield_per_plant_ounces = (cleaned.yield_per_plant_ounces / 35.274).toFixed(2)
            normalized.minimumTemp           = ((cleaned.minimumTemp - 32) * 5 / 9).toFixed(1)
            normalized.maximumTemp           = ((cleaned.maximumTemp - 32) * 5 / 9).toFixed(1)
            normalized.spacing_between_plants = (cleaned.spacing_between_plants * 2.54 / 100).toFixed(2)
        }
        return normalized
    }

    function normalizePlantForSave(plant) {
        const p = { ...plant }
        if (profile?.unitmeasurement === "metric") {
            p.spaceUsed              = (p.spaceUsed * 10.764).toFixed(2)
            p.estHarvest             = (p.estHarvest * 2.2).toFixed(2)
            p.yield_per_plant_ounces = (p.yield_per_plant_ounces * 35.274).toFixed(2)
            p.minimumTemp            = ((p.minimumTemp * 9 / 5) + 32).toFixed(1)
            p.maximumTemp            = ((p.maximumTemp * 9 / 5) + 32).toFixed(1)
            p.spacing_between_plants = (p.spacing_between_plants * 100 / 2.54).toFixed(2)
        }
        return p
    }

    function makeDateColumn(header, field) {
        return {
            headerName: header, field, editable: true,
            cellEditor: "agDateCellEditor",
            cellEditorParams: (params) => ({ useBrowserDatePicker: true, value: params.value ? dayjs(params.value, "M/D/YYYY").toDate() : null }),
            valueParser: (params) => { const d = dayjs(params.newValue); return d.isValid() ? d.format("M/D/YYYY") : params.oldValue },
            valueFormatter: (params) => { if (!params.value) return ""; const d = dayjs(params.value, ["M/D/YYYY","YYYY-MM-DD"]); return d.isValid() ? d.format("M/D/YYYY") : params.value },
            width: 150,
        }
    }

    const plantColumnCatalog = {
        species:       { headerName: "Plant 🔒", field: "species", editable: false },
        variety:       { headerName: "Variety", field: "variety", editable: true },
        numPlants:     { headerName: "# of Plants", field: "numPlants", editable: true },
        area:          { headerName: "Area", field: "area", editable: true, cellEditor: "agSelectCellEditor", cellEditorParams: (areas) => ({ values: areas.map(a => a.name) }) },
        season:        { headerName: "Life Cycle", field: "season", editable: true, cellEditor: "agSelectCellEditor", cellEditorParams: { values: ["annual","biennial","perennial"] } },
        season_rotation: { headerName: "Season", field: "season_rotation", editable: true, cellEditor: "agSelectCellEditor", cellEditorParams: { values: ["","Spring","Summer","Fall","Winter"] } },
        status:        { headerName: "Status", field: "status", editable: true, cellEditor: "agSelectCellEditor", cellEditorParams: { values: ["Purchased","Not Started","Seed Starting","Transplanting","Growing","Harvesting","Failed","Completed"] } },
        plantFamily:   { headerName: "Plant Family", field: "plant_family", editable: true },
        seedStartDate: makeDateColumn("Seed Start Date", "seedStartDate"),
        transplantDate: makeDateColumn("Transplant Date", "transplantDate"),
        harvestDate:   makeDateColumn("Harvest Date", "harvestDate"),
        guildFunction: { headerName: "Guild Function 🔒", field: "guildFunction", editable: false, valueFormatter: p => Array.isArray(p.value) ? p.value.join(", ") : p.value },
        foodForestLayer: { headerName: "Food Forest Layer 🔒", field: "foodForestLayer", editable: false, valueFormatter: p => Array.isArray(p.value) ? p.value.join(", ") : p.value },
        climate:       { headerName: "Climate 🔒", field: "climate", editable: false, valueFormatter: p => Array.isArray(p.value) ? p.value.join(", ") : p.value },
        usdaMin: {
            headerName: "USDA Zone Min", field: "USDAMin", editable: true, cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["1","2","3","4","5","6","7","8","9","10","11","12"] },
            valueFormatter: p => ({ 1:"1a-1b",2:"2a-2b",3:"3a-3b",4:"4a-4b",5:"5a-5b",6:"6a-6b",7:"7a-7b",8:"8a-8b",9:"9a-9b",10:"10a-10b",11:"11a-11b",12:"12a-12b" }[p.value] || p.value),
            valueParser: p => Number(p.newValue),
        },
        usdaMax: {
            headerName: "USDA Zone Max", field: "USDAMax", editable: true, cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["1","2","3","4","5","6","7","8","9","10","11","12"] },
            valueFormatter: p => ({ 1:"1a-1b",2:"2a-2b",3:"3a-3b",4:"4a-4b",5:"5a-5b",6:"6a-6b",7:"7a-7b",8:"8a-8b",9:"9a-9b",10:"10a-10b",11:"11a-11b",12:"12a-12b" }[p.value] || p.value),
            valueParser: p => { const n = parseInt(p.newValue, 10); return isNaN(n) ? null : n },
        },
        yieldPerPlant: { headerName: `Yield Per Plant (${labels[unitmeasurement].weight})`, field: "yield_per_plant_ounces", editable: true },
        spacing:       { headerName: `Spacing Between Plants (${labels[unitmeasurement].distance})`, field: "spacing_between_plants", editable: true },
        spaceUsed:     { headerName: `Amount of Space Used (${labels[unitmeasurement].space}) 🔒`, field: "spaceUsed", editable: false },
        totalHarvest:  { headerName: `Total Harvest (${labels[unitmeasurement].weight}) 🔒`, field: "totalHarvest", editable: false },
        minimumTemp:   { headerName: `Minimum Temperature (${labels[unitmeasurement].temperature})`, field: "minimumTemp", editable: true },
        maximumTemp:   { headerName: `Maximum Temperature (${labels[unitmeasurement].temperature})`, field: "maximumTemp", editable: true },
        germinationTime: { headerName: "Days to Germinate", field: "germinationTime", editable: true },
    }

    // ── event / calendar helpers ─────────────────────────────────────────────
    function createNewEvent(plant, title, category, startDate) {
        const ev = { id: uuidv4(), username, title, description: "", plant: plant.plant, start: startDate, category }
        if (plant.events) plant.events.push(ev.id)
        onAddNewEvent(ev)
    }

    function monthSpanToDates(startMonth, endMonth, baseYear) {
        const start = new Date(baseYear, startMonth, 1)
        let endYear = baseYear
        if (endMonth < startMonth) endYear += 1
        const end = new Date(endYear, endMonth + 1, 0)
        return { start, end }
    }

    function daysBetween(d1, d2) {
        return Math.ceil(Math.abs(new Date(d2) - new Date(d1)) / (24 * 60 * 60 * 1000))
    }

    function calcExactDay(keyMonth, min, max, inv, climateInfo, baseYear) {
        const xs = [0, 30, 60]
        const prevMonth = (keyMonth + 11) % 12
        const nextMonth = (keyMonth + 1) % 12
        const ymins = [climateInfo[months[prevMonth]+"Min"], climateInfo[months[keyMonth]+"Min"], climateInfo[months[nextMonth]+"Min"]].map(parseFloat)
        const ymaxs = [climateInfo[months[prevMonth]+"Max"], climateInfo[months[keyMonth]+"Max"], climateInfo[months[nextMonth]+"Max"]].map(parseFloat)
        const minSpline = new Spline(xs, ymins)
        const maxSpline = new Spline(xs, ymaxs)
        let keyDate = null
        if (!inv) { for (let i = 0;  i <= 60; i++) { if (minSpline.at(i) >= min && maxSpline.at(i) <= max) { keyDate = i; break } } }
        else      { for (let i = 60; i >= 0;  i--) { if (minSpline.at(i) >= min && maxSpline.at(i) <= max) { keyDate = i; break } } }
        if (keyDate == null) return null
        let prevYear = baseYear
        if (prevMonth > keyMonth && seasonRotation !== "Winter") prevYear -= 1
        const d = new Date(prevYear, prevMonth, 15)
        d.setDate(d.getDate() + keyDate)
        return d
    }

    function calcPlantingCalendar(plant) {
        calcMetaRef.current = null
        const plantInfo = { ...plant }
        let min = parseFloat(plantInfo.minimumTemp)
        let max = parseFloat(plantInfo.maximumTemp)
        let climateInfo = Object.assign({}, climateData)
        if (profile?.climates?.length > 0) {
            const sel = profile.climates.find(c => c.selected)
            if (sel) climateInfo = sel
        } else if (unitmeasurement === "metric") { min = min * 9/5 + 32; max = max * 9/5 + 32 }

        const seasonRanges = { Fall:[9,10,11], Winter:[0,1,2], Spring:[3,4,5], Summer:[6,7,8] }
        const seasonMonths = seasonRanges[seasonRotation]
        let plWindow = []
        for (let i = 0; i < months.length; i++) {
            if (parseFloat(climateInfo[months[i]+"Min"]) >= min && parseFloat(climateInfo[months[i]+"Max"]) <= max) plWindow.push(i)
        }

        const climateSeries = [
            { name:"Hot Averages", color:"#F00505", data: months.map(m => parseFloat(climateInfo[m+"Max"])) },
            { name:"Cold Averages", color:"#143FA1", data: months.map(m => parseFloat(climateInfo[m+"Min"])) },
        ]
        setClimateAverages(climateSeries)

        if (plWindow.length < 2) { setPlantingDates(false); setPlantingCalendarModal(true); return }

        let ranges = []
        if (plWindow.length > 0) {
            let start = plWindow[0], pointer = start
            for (let i = 1; i < plWindow.length; i++) {
                const cur = plWindow[i]
                if (cur === pointer + 1 || (pointer === 11 && cur === 0)) { pointer = cur }
                else { ranges.push({ start, end: pointer }); start = cur; pointer = cur }
            }
            ranges.push({ start, end: pointer })
        }
        ranges = ranges.map(r => {
            const adj = seasonMonths.includes(r.start) ? r.start : seasonMonths.find(m => m >= r.start) || seasonMonths[0]
            return seasonMonths.includes(adj) ? { start: adj, end: r.end } : null
        }).filter(Boolean)

        let annot = {}
        if (ranges.length > 0) {
            const daysToTransplant = parseInt(plantInfo.daysToTransplant) || 0
            const coarse = monthSpanToDates(ranges[0].start, ranges[0].end, currYear)
            let sMonth = ranges[0].start, sYear = coarse.start.getFullYear(), startDay = null
            do { startDay = calcExactDay(sMonth, min, max, false, climateInfo, sYear); if (!startDay || isNaN(startDay)) { sMonth = (sMonth+1)%12; if (sMonth===0) sYear++ } } while (!(startDay instanceof Date && !isNaN(startDay)))
            let endDay = calcExactDay(ranges[0].end, min, max, true, climateInfo, coarse.end.getFullYear())
            if (endDay < startDay) endDay.setFullYear(endDay.getFullYear() + 1)
            const numDays = (endDay - startDay) / (1000*3600*24)

            if (numDays > parseFloat(plantInfo.daysToHarvest)) {
                annot = { xaxis:[{ x:startDay.getTime(), x2:endDay.getTime(), borderColor:"#00E396", fillColor:"#00E396", opacity:0.2 }], yaxis:[{ y:min, y2:max, borderColor:"#00E396", fillColor:"#00E396", opacity:0.2 }] }
                setAnnotations(annot)
                const today = new Date()
                const fmt = d => `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`
                if (!lastDatePossible) {
                    const harvest = new Date(startDay); harvest.setDate(harvest.getDate() + parseInt(plantInfo.daysToHarvest))
                    const seed = new Date(startDay); seed.setDate(seed.getDate() - daysToTransplant - parseInt(plantInfo.germinationTime))
                    const transplant = new Date(startDay)
                    if (seed < today) {
                        const dayMs = 1000 * 60 * 60 * 24
                        const deltaDays = Math.ceil((today - seed) / dayMs)
                        const projectedHarvest = new Date(harvest); projectedHarvest.setDate(projectedHarvest.getDate() + deltaDays)
                        if (projectedHarvest <= endDay) {
                            // Shift forward so seed starts today, still fitting this year's window.
                            [seed, transplant, harvest].forEach(d => d.setDate(d.getDate() + deltaDays))
                        } else {
                            // Can't complete within this year's window → use next year's.
                            [seed, transplant, harvest].forEach(d => d.setFullYear(d.getFullYear() + 1))
                        }
                    }
                    plant.seedStartDate = fmt(seed); plant.transplantDate = fmt(transplant); plant.harvestDate = fmt(harvest)
                    calcMetaRef.current = { seed: new Date(seed), transplant: new Date(transplant), harvest: new Date(harvest), windowEnd: new Date(endDay) }
                    createNewEvent(plant, `${plant.species} - Seed Starting`, "Seed Starting", seed)
                    createNewEvent(plant, `${plant.species} - Transplanting`, "Transplanting", transplant)
                    createNewEvent(plant, `${plant.species} - Harvesting`, "Harvest", harvest)
                } else {
                    const harvest = new Date(endDay)
                    const transplant = new Date(endDay); transplant.setDate(transplant.getDate() - parseInt(plantInfo.daysToHarvest))
                    const seed = new Date(endDay); seed.setDate(seed.getDate() - daysToTransplant - parseInt(plantInfo.daysToHarvest) - parseInt(plantInfo.germinationTime))
                    if (seed < today) {
                        const dayMs = 1000 * 60 * 60 * 24
                        const deltaDays = Math.ceil((today - seed) / dayMs)
                        const projectedHarvest = new Date(harvest); projectedHarvest.setDate(projectedHarvest.getDate() + deltaDays)
                        if (projectedHarvest <= endDay) {
                            // Shift forward so seed starts today, still fitting this year's window.
                            [seed, transplant, harvest].forEach(d => d.setDate(d.getDate() + deltaDays))
                        } else {
                            // Can't complete within this year's window → use next year's.
                            [seed, transplant, harvest].forEach(d => d.setFullYear(d.getFullYear() + 1))
                        }
                    }
                    plant.seedStartDate = fmt(seed); plant.transplantDate = fmt(transplant); plant.harvestDate = fmt(harvest)
                    calcMetaRef.current = { seed: new Date(seed), transplant: new Date(transplant), harvest: new Date(harvest), windowEnd: new Date(endDay) }
                    createNewEvent(plant, `${plant.species} - Seed Starting`, "Seed Starting", seed)
                    createNewEvent(plant, `${plant.species} - Transplanting`, "Transplanting", transplant)
                    createNewEvent(plant, `${plant.species} - Harvesting`, "Harvest", harvest)
                }
                setPlantingDates(true)
            } else {
                plWindow = { start: startDay.toDateString(), end: endDay.toDateString(), diff: daysBetween(startDay, endDay) }
                setPlantingDates(false); setPlantingMonths(plWindow); setPlantingCalendarModal(true)
            }
        } else {
            setPlantingDates(false); setPlantingCalendarModal(true)
        }
    }

    // ── plant CRUD ───────────────────────────────────────────────────────────
    function findPlantIndices(areas, plantId) {
        for (let i = 0; i < areas.length; i++) {
            const j = areas[i].plants.findIndex(p => p.id === plantId)
            if (j !== -1) return { areaIndex: i, plantIndex: j }
        }
        return { areaIndex: -1, plantIndex: -1 }
    }

    function setAreaSelected(name) {
        setAreaSelectedState(name)
        // Match the area for the year being viewed — names can repeat across years
        // (e.g. once a next-year area has been created), so name alone is ambiguous.
        const found = userareas.find(a => a.name === name && a.year === currYear)
        if (found) setCurrArea(found)
    }

    function updateSpecies(species) {
        const newPlant = { ...currPlant, species }
        if (plantMap[species]) {
            Object.keys(plantProps).forEach(k => { newPlant[k] = plantMap[species][k] })
        } else {
            const cp = customplants.find(p => p.species === species)
            if (cp) {
                Object.assign(newPlant, {
                    daysToHarvest: cp.daysToHarvest, daysToTransplant: cp.daysToTransplant,
                    germinationTime: cp.germinationTime, minimumTemp: cp.minimumTemperature,
                    maximumTemp: cp.maximumTemperature, yield_per_plant_ounces: cp.yieldPerPlant,
                    plant_family: cp.plantFamily, season: cp.season_rotation, size: cp.size,
                    spacing_between_plants: cp.spacing_between_plants, guildFunction: cp.guildFunction,
                    foodForestLayer: cp.foodForestLayer, climate: cp.climate,
                    USDAMin: cp.USDAMin, USDAMax: cp.USDAMax,
                    designerIconKey: cp.designerIconKey, cardIconKey: cp.cardIconKey, isCustomPlant: true,
                })
            }
        }
        setCurrPlant(newPlant)
    }

    function cancelNewPlant() { setAddPlantModal(false) }

    function addPlantToSpreadsheet() {
        const newPlant = { species:"", variety:"", id:uuidv4(), icon:"", seedStartDate:"", transplantDate:"", harvestDate:"", status:"Seed Starting", numPlants:"", harvests:[], notes:[], harvestTotals:0 }
        setCurrPlant(newPlant)
        setSuccession(false)
        setAddPlantModal(true)
    }

    // Build the succession plantings that follow the first one, stepping by the chosen
    // interval (or "consecutive" = next transplant 1 week after the prior harvest) and
    // stopping once a planting can't finish before the window closes. Each is its own plant.
    function buildSuccessionPlants(base, meta) {
        const germ = parseInt(base.germinationTime || 0)
        const dtt  = parseInt(base.daysToTransplant || 0)
        const dth  = parseInt(base.daysToHarvest || 0)
        const consecutive = successionWeeks === "consecutive"
        const stepDays = (parseInt(successionWeeks) || 2) * 7
        const fmt = d => `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`
        const windowEnd = meta.windowEnd
        const extras = []
        let curSeed = new Date(meta.seed), curHarvest = new Date(meta.harvest)
        for (let i = 0; i < 26; i++) {   // defensive cap
            let s
            if (consecutive) { s = new Date(curHarvest); s.setDate(s.getDate() + 7 - (germ + dtt)) }
            else { s = new Date(curSeed); s.setDate(s.getDate() + stepDays) }
            const t = new Date(s); t.setDate(t.getDate() + germ + dtt)
            const h = new Date(t); h.setDate(h.getDate() + dth)
            if (h > windowEnd) break
            const clone = { ...base, id: uuidv4(), events: [], harvests: [], notes: [], harvestTotals: 0, totalHarvest: 0,
                seedStartDate: fmt(s), transplantDate: fmt(t), harvestDate: fmt(h) }
            createNewEvent(clone, `${clone.species} - Seed Starting`, "Seed Starting", s)
            createNewEvent(clone, `${clone.species} - Transplanting`, "Transplanting", t)
            createNewEvent(clone, `${clone.species} - Harvesting`, "Harvest", h)
            extras.push(clone)
            curSeed = s; curHarvest = h
        }
        return extras
    }

    function createNewPlant() {
        const errs = {}
        if (!numPlants) errs.numPlants = "Number of Plants is required"
        if (!plantStatus) errs.status = "Plant Status is required"
        if (plantStatus === "Purchased" && !plantSource) errs.plantSource = "Plant Source is required"
        if (plantStatus === "Purchased" && !orderDate) errs.orderDate = "Order Date is required"
        if (!currPlant.species) errs.species = "Plant species is required"
        if ((currPlant.season === "annual" || currPlant.season === "biennial") && !seasonRotation) errs.season = "Season is required for annuals/biennials"
        if (Object.keys(errs).length) { setErrors(errs); return }

        const plant = { ...currPlant, variety, numPlants, status: plantStatus, season_rotation: seasonRotation }
        if (plantStatus === "Purchased") Object.assign(plant, { plantSource, orderDate, expectedDeliveryDate })
        const numPerSqFt = Math.pow(12 / (plant.spacing_between_plants || 1), 2)
        plant.spaceUsed = (numPlants / numPerSqFt).toFixed(2)
        plant.totalHarvest = 0
        plant.estHarvest = plant.yield_per_plant_ounces ? plant.yield_per_plant_ounces * numPlants / 16.0 : 0
        if (profile?.unitmeasurement === "metric") {
            plant.spaceUsed              = (plant.spaceUsed / 10.764).toFixed(2)
            plant.estHarvest             = (plant.estHarvest / 2.2).toFixed(2)
            plant.yield_per_plant_ounces = (plant.yield_per_plant_ounces / 35.274).toFixed(2)
            plant.minimumTemp            = ((plant.minimumTemp - 32) * 5 / 9).toFixed(2)
            plant.maximumTemp            = ((plant.maximumTemp - 32) * 5 / 9).toFixed(2)
            plant.spacing_between_plants = (plant.spacing_between_plants / 2.54).toFixed(2)
        }
        const dth = parseInt(plant.daysToHarvest || 0)
        let extras = []
        if (plant.season === "annual" || plant.season === "biennial") {
            plant.events = []
            calcPlantingCalendar(plant)
            if (succession && dth > 0 && dth < 65 && calcMetaRef.current) {
                extras = buildSuccessionPlants(plant, calcMetaRef.current)
            }
        }
        const area = { ...currArea, plants: [...(currArea.plants || []), plant, ...extras] }
        onUpdateArea(area)
        setAddPlantModal(false)
        setErrors({})
    }

    function deletePlant(areaId, plantIndex) {
        const area = userareas.find(a => a.id === areaId)
        if (!area) return
        const newArea = { ...area, plants: [...area.plants] }
        const [plant] = newArea.plants.splice(plantIndex, 1)
        if (plant?.events?.length) plant.events.forEach(eid => onDeleteEvent(eid))
        if (designs?.[0]) {
            const updatedDesign = { ...designs[0], plants: designs[0].plants.filter(p => p.id !== plant.id) }
            if (updatedDesign.plants.length !== designs[0].plants.length) onSaveDesign(updatedDesign)
        }
        onUpdateArea(newArea)
    }

    function cellChanged(data) { newCellData = data }

    function onCellValueChanged(params) {
        const { colDef, oldValue, newValue } = params
        const field = colDef.field
        const isDate = ["seedStartDate","transplantDate","harvestDate"].includes(field)
        let fOld = isDate ? (dayjs(oldValue, ["M/D/YYYY","YYYY-MM-DD"]).isValid() ? dayjs(oldValue, ["M/D/YYYY","YYYY-MM-DD"]).format("M/D/YYYY") : "") : String(oldValue ?? "").trim()
        let fNew = isDate ? (dayjs(newValue).isValid() ? dayjs(newValue).format("M/D/YYYY") : "") : String(newValue ?? "").trim()
        if (fOld === fNew) return

        const updatedRow = normalizePlantForSave(params.data)
        if (field === "USDAMin" || field === "USDAMax") { const n = parseInt(newValue, 10); if (!isNaN(n)) updatedRow[field] = n }

        if (field === "area") {
            const oldArea = userareas.find(a => a.plants.some(p => p.id === updatedRow.id))
            const newArea = userareas.find(a => a.name === updatedRow.area)
            if (!newArea) return
            if (oldArea && oldArea.id !== newArea.id) {
                oldArea.plants = oldArea.plants.filter(p => p.id !== updatedRow.id)
                onUpdateArea(oldArea)
            }
            const idx = newArea.plants.findIndex(p => p.id === updatedRow.id)
            if (idx >= 0) newArea.plants[idx] = { ...newArea.plants[idx], ...updatedRow }
            else newArea.plants.push(updatedRow)
            onUpdateArea(newArea)
            return
        }

        const area = userareas.find(a => a.name === updatedRow.area)
        if (!area) return
        const pIdx = area.plants.findIndex(p => p.id === updatedRow.id)
        if (pIdx === -1) return
        area.plants[pIdx] = { ...area.plants[pIdx], [field]: updatedRow[field] }
        onUpdateArea(area)
    }

    function addHarvest(plant) {
        const { areaIndex, plantIndex } = findPlantIndices(userareas, plant.id)
        if (areaIndex === -1 || plantIndex === -1) return
        // Read from the CURRENT snapshot's plant (which has all persisted harvests),
        // not the possibly-stale modal row, so each add appends instead of replacing.
        const area = userareas[areaIndex]
        const current = area.plants[plantIndex]
        const existing = Array.isArray(current.harvests) ? current.harvests : []
        const newHarvests = [...existing, { date: harvestDate, amount: harvestAmount }]
        const totalHarvest = newHarvests.reduce((sum, h) => sum + parseFloat(h.amount || 0), 0)
        // keep the open modal's row in sync for immediate display
        plant.harvests = newHarvests
        plant.totalHarvest = totalHarvest
        const updatedArea = { ...area, plants: area.plants.map((p, i) => i === plantIndex ? { ...p, harvests: newHarvests, totalHarvest } : p) }
        onUpdateArea(updatedArea)
    }

    const deleteHarvest = (date, plant) => {
        const { areaIndex, plantIndex } = findPlantIndices(userareas, plant.id)
        if (areaIndex === -1 || plantIndex === -1) return
        const area = userareas[areaIndex]
        const current = area.plants[plantIndex]
        const existing = Array.isArray(current.harvests) ? current.harvests : []
        const idx = existing.findIndex(h => h.date === date)
        if (idx === -1) return
        const newHarvests = existing.filter((_, i) => i !== idx)
        const totalHarvest = newHarvests.reduce((sum, h) => sum + parseFloat(h.amount || 0), 0)
        plant.harvests = newHarvests
        plant.totalHarvest = totalHarvest
        const updatedArea = { ...area, plants: area.plants.map((p, i) => i === plantIndex ? { ...p, harvests: newHarvests, totalHarvest } : p) }
        onUpdateArea(updatedArea)
    }

    function addPlantLog(plant) {
        const area = userareas.find(a => a.plants?.some(p => p.id === plant.id))
        if (!area) return
        const date = noteDate ? dayjs(noteDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
        if (selectedFiles.length > 0) setSelectedFiles([])
        onCreateJournalEntry({ username, area_id: area.id, plant_id: plant.id, note_text: notes || "", tags: noteTags || "", date, photos: [] })
    }

    const deleteNote = (noteId) => { onDeleteJournalEntry(noteId) }

    // ── year controls ────────────────────────────────────────────────────────
    const handlePrevYear = () => setCurrYear(y => y - 1)
    const handleNextYear = () => setCurrYear(y => y + 1)

    // ── render ───────────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            <Container fluid>
                {/* Year + filter controls */}
                <Row className="mb-3 align-items-center">
                    <Col xs="auto">
                        <button type="button" className="btn btn-sm" style={{ background: "none", border: "none", color: "#212529", padding: "0 4px" }} onClick={handlePrevYear}>
                            <i className="bx bx-chevron-left" style={{ fontSize: 20 }} />
                        </button>
                        <span style={{ fontWeight: 600, fontSize: 16 }}>{currYear}</span>
                        <button type="button" className="btn btn-sm" style={{ background: "none", border: "none", color: "#212529", padding: "0 4px" }} onClick={handleNextYear}>
                            <i className="bx bx-chevron-right" style={{ fontSize: 20 }} />
                        </button>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <PlantGrid
                            tableData={tableData}
                            onCellEdit={cellChanged}
                            areas={userareas}
                            plantColumnCatalog={plantColumnCatalog}
                            addPlantToSpreadsheet={addPlantToSpreadsheet}
                            deletePlant={deletePlant}
                            unitmeasurement={unitmeasurement}
                            addHarvest={addHarvest}
                            deleteHarvest={deleteHarvest}
                            showCompleted={showCompleted}
                            setShowCompleted={setShowCompleted}
                            harvestDate={harvestDate} setHarvestDate={setHarvestDate}
                            harvestUnit={harvestUnit} setHarvestUnit={setHarvestUnit}
                            harvestAmount={harvestAmount} setHarvestAmount={setHarvestAmount}
                            noteDate={noteDate} setNoteDate={setNoteDate}
                            notes={notes} setNotes={setNotes}
                            noteTags={noteTags} setNoteTags={setNoteTags}
                            currYear={currYear} setCurrYear={setCurrYear}
                            addPlantLog={addPlantLog}
                            deleteNote={deleteNote}
                            onUpdateArea={onUpdateArea}
                            selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles}
                            timelineNotes={timelineNotes}
                            onCreateJournalEntry={onCreateJournalEntry}
                            onUpdateJournalEntry={onUpdateJournalEntry}
                            username={username}
                            onCellValueChanged={onCellValueChanged}
                            defaultColDef={{ flex:1, resizable:true, autoHeaderHeight:true, wrapHeaderText:true }}
                        />
                    </Col>
                </Row>
            </Container>

            {/* Add Plant Modal */}
            <Modal isOpen={addPlantModal} toggle={cancelNewPlant}>
                <ModalHeader tag="h4">New Plant</ModalHeader>
                <ModalBody>
                    <Form>
                        <Row className="mb-2">
                            <Col><b>Area</b></Col>
                            <Col>
                                <Input type="select" onChange={e => setAreaSelected(e.target.value)}>
                                    <option value=""></option>
                                    {userareas.filter(a => a.year === currYear).map(a => (
                                        <option key={a.id} value={a.name}>{a.name}</option>
                                    ))}
                                </Input>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col><b>Species</b></Col>
                            <Col>
                                <PlantSelect
                                    plantOptions={plantOptions.map(p => ({ key: p.value, label: p.value }))}
                                    value=""
                                    onChange={val => updateSpecies(val)}
                                />
                                <span style={{ color:"#FF0000" }}>{errors.species}</span>
                            </Col>
                        </Row>
                        {currPlant.season !== "perennial" && (
                            <Row className="mb-2">
                                <Col md={6}>Season</Col>
                                <Col md={6}>
                                    <Input type="select" onChange={e => setSeasonRotation(e.target.value)}>
                                        <option value=""></option>
                                        <option>Spring</option><option>Summer</option><option>Fall</option><option>Winter</option>
                                    </Input>
                                    <span style={{ color:"#FF0000" }}>{errors.season}</span>
                                </Col>
                            </Row>
                        )}
                        <Row className="mb-2">
                            <Col md={6}><b>Variety</b></Col>
                            <Col md={6}><Input type="text" value={variety} onChange={e => setVariety(e.target.value)} /></Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={6}><b>Growing Phase</b></Col>
                            <Col md={6}>
                                <Input type="select" onChange={e => setPlantStatus(e.target.value)}>
                                    <option value=""></option>
                                    {["Purchased","Not Started","Seed Starting","Transplanting","Growing","Harvest","Failed","Completed"].map(s => <option key={s}>{s}</option>)}
                                </Input>
                                <span style={{ color:"#FF0000" }}>{errors.status}</span>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}><b>Number of Plants</b></Col>
                            <Col md={6}>
                                <Input type="text" value={numPlants} onChange={e => setNumPlants(e.target.value)} />
                                <span style={{ color:"#FF0000" }}>{errors.numPlants}</span>
                            </Col>
                        </Row>
                        {currPlant.season !== "perennial" && parseInt(currPlant.daysToHarvest || 0) > 0 && parseInt(currPlant.daysToHarvest || 0) < 65 && (
                            <Row className="mb-3">
                                <Col md={6}><b>Succession plant</b></Col>
                                <Col md={6}>
                                    <Input type="checkbox" checked={succession} onChange={e => setSuccession(e.target.checked)} style={{ marginTop:0 }} />
                                    {succession && (
                                        <Input type="select" className="mt-2" value={successionWeeks} onChange={e => setSuccessionWeeks(e.target.value)}>
                                            <option value="2">Every 2 weeks</option>
                                            <option value="3">Every 3 weeks</option>
                                            <option value="4">Every 4 weeks</option>
                                            <option value="consecutive">Consecutive (after harvest)</option>
                                        </Input>
                                    )}
                                </Col>
                            </Row>
                        )}
                        {plantStatus === "Purchased" && (<>
                            <Row className="mb-2">
                                <Col md={6}><b>Source</b></Col>
                                <Col md={6}><Input type="text" value={plantSource} onChange={e => setPlantSource(e.target.value)} /></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={6}><b>Order Date</b></Col>
                                <Col md={6}><Input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} /></Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={6}><b>Expected Delivery</b></Col>
                                <Col md={6}><Input type="text" value={expectedDeliveryDate} onChange={e => setExpectedDeliveryDate(e.target.value)} /></Col>
                            </Row>
                        </>)}
                        <Row className="mt-3">
                            <Col md={4} />
                            <Col md={2}><Button type="button" size="sm" style={{ backgroundColor: "#FF9900", borderColor: "#FF9900", color: "#fff" }} onClick={createNewPlant}>Create</Button></Col>
                            <Col md={2}><button type="button" className="btn btn-sm" style={{ backgroundColor: "#fff", borderColor: "#d1d5db", color: "#374151" }} onClick={cancelNewPlant}>Cancel</button></Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>
        </div>
    )
}

const mapStateToProps = (state) => {
    const sortedPlants = [...(state.designer.plants || [])].sort((a, b) => a.name > b.name ? 1 : -1)
    const plantFun = sortedPlants.reduce((map, obj) => { map[obj.name] = obj; return map }, {})
    const filteredAreas = (state.goals.areas || [])
        .map(area => { const a = structuredClone(area); a.plants = a.plants.filter(p => p.species !== undefined); return a })
        .sort((a, b) => a.name.localeCompare(b.name))
    return {
        plantMap:     plantFun,
        plantData:    sortedPlants,
        profile:      state.Profile.user || {},
        userareas:    filteredAreas,
        designs:      state.designer.designs || [],
        climateData:  state.designer.temps || {},
        customplants: state.goals.customplants || [],
        timelineNotes: Array.isArray(state.journal.timelineNotes) ? state.journal.timelineNotes : [],
    }
}

const mapDispatchToProps = dispatch => ({
    onGetProfile:         (username) => dispatch(getProfile(username)),
    onGetPlantData:       ()         => dispatch(getPlantData()),
    onGetAreas:           ()         => dispatch(getAreas()),
    onUpdateArea:         area       => dispatch(updateArea(area)),
    onGetCustomPlants:    ()         => dispatch(fetchCustomPlants()),
    onGetClimateData:     zipcode    => dispatch(getClimateData(zipcode)),
    onAddNewEvent:        event      => dispatch(addNewEvent(event)),
    onDeleteEvent:        id         => dispatch(deleteEvent(id)),
    onSaveDesign:         design     => dispatch(updateDesign(design)),
    onLoadDesigns:        ()         => dispatch(getDesigns()),
    onCreateJournalEntry: data       => dispatch(createJournalEntry(data)),
    onDeleteJournalEntry: noteId     => dispatch(deleteJournalEntry(noteId)),
    onGetJournalTimeline: params     => dispatch(getJournalTimeline(params)),
    onUpdateJournalEntry: data       => dispatch(updateJournalEntry(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PlantsInventory)
